import {useEffect, useRef, useState} from 'react';
import {getSigningStatus, getSigningEventsUrl} from '../api/renewalClickApi';
import {SIGNING_SSE_EVENT} from '../constants/renewalClick';

export const useSigningStatus = (shortLinkKey, enabled, recoveryTrigger, embedded = false) => {
  const [signingCompleted, setSigningCompleted] = useState(false);
  const [finalizationFailed, setFinalizationFailed] = useState(false);
  const sseRef = useRef(null);
  const checkStatusRef = useRef(null);
  const recoveryTriggerRef = useRef(recoveryTrigger);
  recoveryTriggerRef.current = recoveryTrigger;

  useEffect(() => {
    if (!enabled || !shortLinkKey) return;
    let cancelled = false;

    const checkStatus = async () => {
      try {
        const result = await getSigningStatus(shortLinkKey);
        if (!cancelled && result?.signed) setSigningCompleted(true);
      } catch {
        if (!cancelled && embedded && recoveryTriggerRef.current) setFinalizationFailed(true);
      }
    };
    checkStatusRef.current = checkStatus;

    let hasCheckedThisOutage = false;
    try {
      const eventsUrl = getSigningEventsUrl(shortLinkKey);
      const source = new EventSource(eventsUrl, {withCredentials: true});
      sseRef.current = source;

      source.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.event === SIGNING_SSE_EVENT.COMPLETED) {
            setFinalizationFailed(false);
            setSigningCompleted(true);
          } else if (data.event === SIGNING_SSE_EVENT.FINALIZATION_FAILED) {
            // The stream stays open: a webhook retry can still succeed.
            setFinalizationFailed(true);
          }
        } catch {
          // ignore parse errors
        }
      };

      source.onopen = () => {
        hasCheckedThisOutage = false;
        if (embedded) checkStatus();
      };

      source.onerror = () => {
        // SSE reconnects automatically; only recover once per disconnect
        // episode so the browser's native retry cycle doesn't turn this
        // into a polling loop.
        if (!hasCheckedThisOutage) {
          hasCheckedThisOutage = true;
          checkStatus();
        }
      };
    } catch {
      // EventSource unavailable — one-shot fallback check
      checkStatus();
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkStatus();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    if (embedded) checkStatus();

    return () => {
      cancelled = true;
      if (sseRef.current) sseRef.current.close();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, shortLinkKey, embedded]);

  // Recovery check on the one signal that means completion is newly possible
  useEffect(() => {
    if (!recoveryTrigger) return undefined;
    if (embedded) setFinalizationFailed(false);
    checkStatusRef.current?.();
    if (!embedded) return undefined;
    // Bounded recovery covers a callback racing the first status read; never poll indefinitely.
    const timers = [2000, 5000, 15000].map((delay) => setTimeout(() => checkStatusRef.current?.(), delay));
    const timeout = setTimeout(() => setFinalizationFailed(true), 30000);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(timeout);
    };
  }, [recoveryTrigger, embedded]);

  useEffect(() => {
    if (signingCompleted) {
      if (sseRef.current) sseRef.current.close();
    }
  }, [signingCompleted]);

  return {signingCompleted, finalizationFailed};
};
